import ListField from '../components/ListField';
import { FaFilePdf } from "react-icons/fa";
import "../styles/processForm.css";
import IntegratedPersonPhotoInput, { isIntegratedPersonPhoto } from "../components/IntegratedPersonPhotoInput";
import React, { useEffect, useState } from "react";
import api from "../api";
import FileAttachment from "../components/FileAttachment";
import DecimalInput from "../components/DecimalInput";
import IntegerInput from "../components/IntegerInput";
import MaskedInput from "../components/MaskedInput";
import { createRequestPdf } from "../utils/requestPdfExport";

export default function CompletedRequestView({ instance, step }) {
  const [form, setForm] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [histRes, formRes] = await Promise.all([
          api.get(`/processes/instances/${instance.id}/history`),
          instance.process.formId
            ? api.get(`/forms/${instance.process.formId}`)
            : Promise.resolve({ data: null }),
        ]);
        setHistory(histRes.data.steps || []);
        setForm(formRes.data);
      } catch {
        setForm(null);
        setHistory([]);
      } finally {
        setLoading(false);
      }
    };
    if (instance) {
      setLoading(true);
      fetchData();
    }
  }, [instance]);

  if (!instance) return null;
  if (loading) return <p>Carregando...</p>;
  if (!form) return <p>Nenhum formulário para exibir.</p>;

  const fields = form.schema?.fields || [];
  const groups = form.schema?.groups || [];

  const limit =
    typeof step === "number" && step >= 0
      ? Math.min(step + 1, history.length)
      : history.length;
  let aggregated = {};
  for (let i = 0; i < limit; i++) {
    aggregated = { ...aggregated, ...(history[i].data || {}) };
  }

  const handleDownloadPdf = () => {
    const doc = createRequestPdf({ instance, form, values: aggregated });
    doc.save(`solicitacao-${instance.id}.pdf`);
  };

  return (
    <div className="space-y-6">
      <div className="process-request-heading">
        <h1 className="process-request-title">
          Solicitação {instance.id} - {instance.process.name}
        </h1>
        <button
          onClick={handleDownloadPdf}
          className="process-primary-button inline-flex items-center gap-2"
        >
          <FaFilePdf aria-hidden="true" /> Baixar PDF
        </button>
      </div>
      <div className="grid grid-cols-4 gap-2 bg-gray-50 p-2 rounded shadow text-sm">
        <div>
          <span className="font-medium">ID:</span> {instance.id}
        </div>
        <div>
          <span className="font-medium">Processo:</span> {instance.process.name}
        </div>
        <div>
          <span className="font-medium">Solicitante:</span>{" "}
          {instance.requester?.name ||
            instance.user?.name ||
            instance.startedBy?.name ||
            "-"}
        </div>
        <div>
          <span className="font-medium">Início:</span>{" "}
          {new Date(instance.startedAt).toLocaleString()}
        </div>
      </div>
      {groups.map((g) => {
        const gFields = fields.filter(
          (f) =>
            f.groupId === g.id &&
            Object.prototype.hasOwnProperty.call(aggregated, f.id),
        );
        if (gFields.length === 0) return null;
        return (
          <div key={g.id} className="mb-4">
            <h3 className="text-lg font-medium mb-1">{g.label}</h3>
            <div className="grid grid-cols-12 gap-2 bg-gray-50 p-4 rounded shadow">
              {gFields.map((f, idx) => (
                <div
                  key={`${g.id}-${idx}`}
                  className="flex flex-col"
                  style={{ gridColumn: `span ${f.size} / span ${f.size}` }}
                >
                  <label className="font-medium">
                    {f.label}
                    {f.required && <span className="text-red-600"> *</span>}
                  </label>
                  {f.type === "list" ? (
                    <ListField field={f} value={aggregated[f.id]} disabled />
                  ) : isIntegratedPersonPhoto(f) ? (
                    <IntegratedPersonPhotoInput field={f} value={aggregated[f.id]} readOnly />
                  ) : f.type === "file" ? (
                    <FileAttachment file={aggregated[f.id]} />
                  ) : f.type === "decimal" ? (
                    <DecimalInput
                      decimalPlaces={f.decimalPlaces || 0}
                      value={aggregated[f.id]}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "number" ? (
                    <IntegerInput
                      value={aggregated[f.id]}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "phone" ? (
                    <MaskedInput
                      mask="phone"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : (f.type === "cpf" || f.mask === "cpf") ? (
                    <MaskedInput
                      mask="cpf"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "cnpj" ? (
                    <MaskedInput
                      mask="cnpj"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "cep" ? (
                    <MaskedInput
                      mask="cep"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "email" ? (
                    <input
                      type="email"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      inputMode="email"
                      autoComplete="email"
                      pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "date" ? (
                    <input
                      type="date"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "time" ? (
                    <input
                      type="time"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : (
                    <input
                      type={f.type}
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {fields.filter(
        (f) =>
          !f.groupId && Object.prototype.hasOwnProperty.call(aggregated, f.id),
      ).length > 0 && (
        <div className="mb-4">
          {groups.length > 0 && (
            <h3 className="text-lg font-medium mb-1">Campos</h3>
          )}
          <div className="grid grid-cols-12 gap-2 bg-gray-50 p-4 rounded shadow">
            {fields
              .filter(
                (f) =>
                  !f.groupId &&
                  Object.prototype.hasOwnProperty.call(aggregated, f.id),
              )
              .map((f, idx) => (
                <div
                  key={`nogroup-view-${idx}`}
                  className="flex flex-col"
                  style={{ gridColumn: `span ${f.size} / span ${f.size}` }}
                >
                  <label className="font-medium">
                    {f.label}
                    {f.required && <span className="text-red-600"> *</span>}
                  </label>
                  {f.type === "list" ? (
                    <ListField field={f} value={aggregated[f.id]} disabled />
                  ) : isIntegratedPersonPhoto(f) ? (
                    <IntegratedPersonPhotoInput field={f} value={aggregated[f.id]} readOnly />
                  ) : f.type === "file" ? (
                    <FileAttachment file={aggregated[f.id]} />
                  ) : f.type === "decimal" ? (
                    <DecimalInput
                      decimalPlaces={f.decimalPlaces || 0}
                      value={aggregated[f.id]}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "number" ? (
                    <IntegerInput
                      value={aggregated[f.id]}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "phone" ? (
                    <MaskedInput
                      mask="phone"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : (f.type === "cpf" || f.mask === "cpf") ? (
                    <MaskedInput
                      mask="cpf"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "cnpj" ? (
                    <MaskedInput
                      mask="cnpj"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "cep" ? (
                    <MaskedInput
                      mask="cep"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "email" ? (
                    <input
                      type="email"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      inputMode="email"
                      autoComplete="email"
                      pattern="[^@\s]+@[^@\s]+\.[^@\s]+"
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "date" ? (
                    <input
                      type="date"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : f.type === "time" ? (
                    <input
                      type="time"
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  ) : (
                    <input
                      type={f.type}
                      name={f.id}
                      value={aggregated[f.id] || ""}
                      readOnly
                      className="border px-2 py-1 bg-gray-100"
                    />
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
